
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { User } from './types';
import { toast } from "sonner";

export const useUsers = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  // Fetch users from Supabase
  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*');
      
      if (error) {
        throw error;
      }

      if (data) {
        // Transform the data to match our User interface
        const transformedUsers = data.map(profile => ({
          id: profile.id,
          name: profile.name,
          email: profile.email,
          status: profile.has_completed_onboarding ? 'active' : 'pending',
          joinDate: new Date(profile.date_joined).toISOString().split('T')[0],
          progress: 0, // We'd need to fetch this from user_progress table in a real app
          isAdmin: profile.is_admin
        }));
        
        setUsers(transformedUsers);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Filter users based on search term
    const filtered = users.filter(
      user => user.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
              user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setUsers(filtered);
  };

  const handleRefresh = () => {
    fetchUsers();
  };

  // Toggle admin status
  const toggleAdminStatus = async (userId: string, currentStatus: boolean) => {
    try {
      setIsLoading(true);
      
      const { error } = await supabase
        .from('profiles')
        .update({ is_admin: !currentStatus })
        .eq('id', userId);
      
      if (error) {
        throw error;
      }
      
      // Update the local state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, isAdmin: !currentStatus } : user
      ));
      
      toast.success(`Admin status ${!currentStatus ? 'granted' : 'revoked'} successfully`);
      
    } catch (error) {
      console.error('Error updating admin status:', error);
      toast.error('Failed to update admin status');
    } finally {
      setIsLoading(false);
    }
  };

  // Create a new invite
  const handleCreateInvite = async (values: {email: string, makeAdmin: boolean}) => {
    try {
      setIsLoading(true);
      
      // Use parameters that match the type definition
      const { data, error } = await supabase.rpc('create_invite', {
        email: values.email,
        expires_in_days: 7,
        make_admin: values.makeAdmin
      });
      
      if (error) {
        throw error;
      }
      
      toast.success(`Invite sent to ${values.email}`);
      setShowInviteDialog(false);
      
    } catch (error) {
      console.error('Error creating invite:', error);
      toast.error('Failed to create invite');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    searchTerm,
    setSearchTerm,
    users,
    isLoading,
    showInviteDialog,
    setShowInviteDialog,
    handleSearch,
    handleRefresh,
    toggleAdminStatus,
    handleCreateInvite
  };
};
